import { closedDocKey, garmentRgb, hydrateSteps, onDocRestored, onlyPresetChanged, useStore, type AppState, type DocEntry } from '../state/store';
import { createRunner } from '../state/runner';
import { createSepRunner } from '../state/sep-runner';
import { nextGeneration } from '../state/runner';
import { createMockupRunner } from '../state/mockup-runner';
import { separationFor, sepChangeKind } from '../state/sep';
import { mockupChangeKind, mockupFor, sameMockupJob } from '../state/mockup';
import {
  defaultTemplateId, indexedDbTemplateStore, loadBuiltinTemplates, memoryTemplateStore,
  type TemplateSource, type TemplateStore, type TemplateStoreKind,
} from '../lib/templates';
import {
  indexedDbSceneStore, memorySceneStore, sceneName, SCENE_MAX_BYTES,
  type SceneSource, type SceneStore, type SceneStoreKind,
} from '../lib/scenes';
import { backJobFor } from '../lib/mockups';
import { sanitizeJob } from '../state/mockup';
import { sceneIdOf } from '../state/mockup-runner';
import type { MockupJob, MockupTemplate } from '@eip/engine';
import { previews, dropDoc, dropMockupBlank, dropMockupPreview } from '../state/bitmaps';
import { engine, callback, onEngineFatal, transferBytes } from '../worker/client';
import { capabilityMessage, missingCapabilities } from '../lib/capabilities';
import { PREVIEW_MAX } from '../worker/protocol';
import { isSupportedName, newDocId } from '../lib/files';
import { loadPersisted, savePersisted } from '../state/persist';
import { canUndo, forgetUndo, forgetUndoWhere, inModalDialog, isTextEntry, isUndoShortcut, topUndoToken, undo, undoTo, undoneMessage, type UndoScope, type UndoToken } from '../state/undo';

onEngineFatal((message) => useStore.getState().setEngineError(message));

// Startup capability check: an unsupported browser gets one plain sentence
// instead of a cryptic decode error on the first file. Skipped outside a
// browser (the Node test environment has none of these globals).
if (typeof window !== 'undefined') {
  const message = capabilityMessage(missingCapabilities(window as unknown as Parameters<typeof missingCapabilities>[0]));
  if (message) useStore.getState().setEngineError(message);
}

const storage = (() => { try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; } })();
{
  const p = loadPersisted(storage);
  if (p.settings) useStore.getState().setSettings(p.settings);
  if (p.viewer) useStore.getState().setViewer(p.viewer);
  // Steps last, and reconciled against the settings that just landed: the bar
  // owns print width and dpi, so a persisted resize step is re-derived from the
  // VALIDATED settings rather than trusted. Without this, a `targetWidthIn`
  // rejected by `sanitizeSettings` left the resize step behind and the run
  // produced a resized output while the bar showed native.
  const st = useStore.getState();
  st.setSteps(hydrateSteps(p.steps ?? st.steps, st.settings));
  if (p.mode) st.setAppMode(p.mode);
  if (p.sep) st.setSepDefaults(p.sep.defaults);
  if (p.mockup) st.setMockupDefaults(p.mockup.defaults);
}
let saveTimer: ReturnType<typeof setTimeout> | undefined;
const unsubPersist = useStore.subscribe((st, prev) => {
  if (st.settings === prev.settings && st.steps === prev.steps && st.viewer === prev.viewer
    && st.appMode === prev.appMode && st.sepDefaults === prev.sepDefaults && st.mockupDefaults === prev.mockupDefaults) return;
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => savePersisted(storage, {
    settings: st.settings, steps: st.steps, viewer: st.viewer, mode: st.appMode,
    sep: { defaults: st.sepDefaults }, mockup: { defaults: st.mockupDefaults },
  }), 300);
});

export const runner = createRunner(useStore, {
  api: engine(),
  wrap: callback,
  onPreview: (id, which, p) => {
    if (!useStore.getState().docs.some((d) => d.id === id)) { p.bitmap.close(); return; }
    const e = previews.get(id) ?? {}; e[which]?.bitmap.close(); e[which] = p; previews.set(id, e);
  },
});

export const sepRunner = createSepRunner(useStore, { api: engine() });

// Keep every open document's separation in step with the DTF output it
// separates and with its own settings, while in screen-print mode: a moved
// `run.generation` means the art under the plates changed (rebuild), a
// 'rebuild' separation change (mode/inks/white/trapPx) means the same, and a
// 'films' change (screen/film layout) needs no rebuild — just push the new
// settings to the worker (`sepRunner.updateFilms`) and bump `filmsVersion` once
// that lands, so the cached plate/film previews it invalidated refetch current.
//
// Entering screen-print mode (or a doc becoming ready while already in it)
// must not leave a ready document without a separation to build: seed one
// from the shop defaults — inks stay empty until the user detects them, but
// the white/underbase plate and checks exist as soon as the build lands.
const unsubSep = useStore.subscribe((st, prev) => {
  if (st.appMode !== 'sep') return;
  const justEnteredSep = prev.appMode !== 'sep';
  for (const doc of st.docs) {
    const prevDoc = prev.docs.find((d) => d.id === doc.id);
    if (doc.status === 'ready' && !doc.sep && (justEnteredSep || prevDoc?.status !== 'ready')) {
      useStore.getState().setSeparation(doc.id, separationFor(undefined, st.sepDefaults, garmentRgb(st.settings.garmentHex)));
      sepRunner.trigger(doc.id);
      continue;
    }
    // A doc that already HAS a separation can still be out of step on entry: a DTF
    // edit made while in DTF mode landed a new run (the early return above skipped
    // it) and `doc.run === prevDoc.run` across the mode switch, so the run-change
    // branch below never fires either — the separation stayed stale forever, with
    // exports blocked and the panel still reading "Built N plates".
    //
    // No `building` check: at mode entry a redundant build (the one in flight may
    // still be the right one) costs one build, whereas skipping one that was
    // reserved before the newer run leaves the doc stale again — and `reserve`
    // makes the in-flight result stale the moment this trigger lands.
    if (justEnteredSep && doc.status === 'ready' && doc.sep && doc.run
      && (doc.sep.builtGeneration === undefined || doc.sep.runGeneration !== doc.run.generation)) {
      sepRunner.trigger(doc.id);
      continue;
    }
    if (!prevDoc) continue;
    if (doc.run && doc.run !== prevDoc.run) { sepRunner.trigger(doc.id); continue; }
    if (doc.sep && doc.sep.separation !== prevDoc.sep?.separation) {
      const kind = prevDoc.sep ? sepChangeKind(prevDoc.sep.separation, doc.sep.separation) : 'rebuild';
      if (kind === 'rebuild') sepRunner.trigger(doc.id);
      else if (kind === 'films') sepRunner.updateFilms(doc.id);
    }
  }
});

/**
 * Where the user's own templates live. IndexedDB when the browser has it (the
 * photos are 1–3 MB Blobs, far past what localStorage would take); an in-memory
 * store otherwise, so authoring a template still works for the session in a
 * private window or under blocked storage. Exported for the template editor.
 */
export const templateStore: TemplateStore = typeof indexedDB !== 'undefined' ? indexedDbTemplateStore(indexedDB) : memoryTemplateStore();

/**
 * Whether user templates are actually being kept, or only held for this session
 * — the IndexedDB store falls back to memory the first time the database refuses
 * to open. Read by the editor and the panel, which say so in one line rather
 * than letting the shop find out at the next reload.
 *
 * A function, not a constant: the fallback happens on the first store call
 * (`loadTemplates` at startup), which is after this module is evaluated.
 */
export function templateStoreKind(): TemplateStoreKind { return templateStore.kind(); }

/**
 * Rebuild the template registry: the shipped blanks first, then the user's own,
 * which is the order `groupByStyle` and `defaultTemplateId` rely on. Called once
 * at startup and again whenever the editor saves or deletes a template.
 *
 * Browser-only for the built-ins: `loadBuiltinTemplates` fetches a root-relative
 * URL, which has nothing to resolve against in the Node test environment.
 */
export async function loadTemplates(): Promise<void> {
  const builtin = typeof window !== 'undefined' ? await loadBuiltinTemplates(fetch) : [];
  let user: TemplateSource[] = [];
  // A failed IndexedDB read must not cost the shop its built-in blanks too.
  try { user = await templateStore.list(); } catch (err) { console.warn('mockup templates: your own templates could not be read', err); }
  useStore.getState().setTemplates([...builtin, ...user]);
}

/**
 * Where the user's backdrop scenes live — IndexedDB when the browser has it (these are
 * multi-megabyte photographs), an in-memory store otherwise so importing one still works for the
 * session in a private window or under blocked storage. A database of its own, so a blocked upgrade
 * cannot cost the shop its templates. Exported for the backdrop panel.
 */
export const sceneStore: SceneStore = typeof indexedDB !== 'undefined' ? indexedDbSceneStore(indexedDB) : memorySceneStore();

/** Whether scenes are actually being kept, or only held for this session. A function, not a
 *  constant: the fallback happens on the first store call, which is after this module is evaluated. */
export function sceneStoreKind(): SceneStoreKind { return sceneStore.kind(); }

/**
 * Load the scene list into the store. The pixels are NOT decoded here — each row carries its Blob
 * and the worker decodes it on demand (`mockupRunner.ensureScene`), so a shop with a dozen
 * backdrops pays for the ones it actually renders behind.
 *
 * A failed read must not cost the shop anything else on startup: it is warned about and the list
 * stays empty, which reads in the panel as "no scenes yet".
 */
export async function loadScenes(): Promise<void> {
  try { useStore.getState().setScenes(await sceneStore.list()); }
  catch (err) { console.warn('mockup scenes: your backdrops could not be read', err); }
}
if (typeof window !== 'undefined') { void loadTemplates(); void loadScenes(); }

/** A template's photo as bytes: a built-in from `/templates`, a user template out
 *  of the Blob IndexedDB kept. Transferred rather than copied — these are
 *  multi-megabyte photos and the worker takes ownership of them. */
async function fetchPhoto(source: TemplateSource): Promise<Uint8Array> {
  if (source.photoBlob) return transferBytes(new Uint8Array(await source.photoBlob.arrayBuffer()));
  if (!source.photoUrl) throw new Error(`template "${source.template.id}" has no photo`);
  const res = await fetch(source.photoUrl);
  if (!res.ok) throw new Error(`${source.photoUrl}: HTTP ${res.status}`);
  return transferBytes(new Uint8Array(await res.arrayBuffer()));
}

export const mockupRunner = createMockupRunner(useStore, { api: engine(), fetchPhoto });

/** `ensureTemplate` from a subscription. A failed preparation is already on the
 *  template's status and on every document waiting for it, so the rejection is
 *  swallowed here — it exists for the export paths, which do await it. */
const ensureTemplate = (id: string) => { void mockupRunner.ensureTemplate(id).catch(() => {}); };

/** `ensureScene` from a subscription, swallowing the rejection for the same reason: a scene that
 *  cannot be decoded is already on its status and on every document waiting for it. */
const ensureScene = (id: string | null) => { if (id) void mockupRunner.ensureScene(id).catch(() => {}); };

/** A template's maps or a job's backdrop scene landing in the worker on THIS pass — either flip is
 *  the moment a render the slot was already marked as owing becomes possible. */
function becameReady(st: AppState, prev: AppState, templateId: string, sceneId: string | null): boolean {
  if (st.templateStatus[templateId]?.state === 'ready' && prev.templateStatus[templateId]?.state !== 'ready') return true;
  return !!sceneId && st.sceneStatus[sceneId]?.state === 'ready' && prev.sceneStatus[sceneId]?.state !== 'ready';
}

/**
 * `ensureTemplate` with the contract the export paths need: it resolves only
 * once the blank is genuinely prepared, and throws otherwise. `ensureTemplate`
 * itself resolves for a preparation that was superseded (an `unload` under it),
 * which is not an error but is also not a blank the worker can render into.
 */
export async function prepareTemplateForExport(id: string): Promise<void> {
  await mockupRunner.ensureTemplate(id);
  const status = useStore.getState().templateStatus[id];
  if (status?.state !== 'ready') throw new Error(status?.error ?? 'the blank was dropped while it was preparing');
}

/**
 * `ensureScene` with the same contract for the same callers: it resolves only once the scene's
 * pixels are actually in the worker, and throws otherwise — a decode superseded by `unloadScene`
 * (the shop deleting the backdrop mid-export) resolves, which is not an error but is also not a
 * backdrop the compositor can cover a photo with.
 */
export async function prepareSceneForExport(id: string): Promise<void> {
  await mockupRunner.ensureScene(id);
  const status = useStore.getState().sceneStatus[id];
  if (status?.state !== 'ready') throw new Error(status?.error ?? 'the backdrop was dropped while it was loading');
}

/**
 * Land a template authored in the editor: hand the worker's editor entry over to
 * the template's own id, keep the photo, and put it in the registry as ready.
 *
 * The order is the whole of it.
 *  1. IndexedDB FIRST, while nothing has moved: a store that refuses the write
 *     (quota, a private window that blocks it) leaves the session exactly as it
 *     was and the editor shows why, rather than a template that exists until the
 *     next reload.
 *  2. The registry NEXT, and before the unload. Every store write notifies the
 *     mockup subscription synchronously, which calls `ensureTemplate` — so by
 *     the time the id is re-armed there has to be a source carrying the NEW
 *     photo under it, or a document sitting on this template would re-prepare
 *     the OLD one and write its measurements back over ours.
 *  3. `unload` for an id the session already knows: only that re-arms an `error`
 *     status (the Task 2 review's residual) and bumps the runner's token, which
 *     is what stops a preparation still in flight from landing on top of this
 *     save. It is skipped for a brand-new id, where there is nothing to re-arm
 *     and the idle status it writes would only invite a redundant preparation.
 *  4. `mockupSaveEditor`, which is posted before any preparation that step 3 may
 *     have woken can get past its photo read — so the worker moves the editor's
 *     own maps under the new id, and that preparation finds them cached.
 *
 * Returns the template as STORED: `mockupSaveEditor` re-prepares when the saved
 * settings differ from the editor's last preparation, and that can change the
 * separability verdict, which is `recolorable`.
 */
export async function saveUserTemplate(editorId: string, template: MockupTemplate, photo: Blob): Promise<MockupTemplate> {
  const id = template.id;
  const before = useStore.getState();
  const known = before.templates.some((t) => t.template.id === id) || before.templateStatus[id] !== undefined;
  await templateStore.put(template, photo);
  // No `styleName`: the store keeps the template and its photo, not the
  // shop-facing style name, so passing one here would show a group label that
  // the next reload silently replaced with `groupByStyle`'s slug fallback.
  const source: TemplateSource = { kind: 'user', template, photoBlob: photo };
  // Re-read: the registry may have been rebuilt while IndexedDB was writing.
  // An id already listed is replaced IN PLACE — moving it to the end would
  // reshuffle the picker (and `defaultTemplateId`) for a plain re-save.
  putSource(source);
  if (known) await mockupRunner.unload(id);
  const prepared = await engine().mockupSaveEditor(editorId, template);
  // The worker had the last word on whether this blank can be recoloured.
  let saved = template;
  if (prepared.separable !== (template.recolorable !== false)) {
    saved = { ...template, recolorable: prepared.separable };
    await templateStore.put(saved, photo);
    putSource({ ...source, template: saved });
  }
  useStore.getState().setTemplateStatus(id, { state: 'ready', prepared, error: undefined });
  return saved;
}

/** Put one source into the registry, replacing an entry with the same id where
 *  it already sits rather than appending a second (or reordering the list). */
function putSource(source: TemplateSource): void {
  const st = useStore.getState();
  const at = st.templates.findIndex((t) => t.template.id === source.template.id);
  st.setTemplates(at < 0 ? [...st.templates, source] : st.templates.map((t, i) => (i === at ? source : t)));
}

/**
 * Drop a user template: out of IndexedDB, off every document using it, out of
 * the worker and out of the registry — in that order.
 *
 * The documents are repointed BEFORE the registry loses it, or the write that
 * removes it would leave them naming a template nothing can find and
 * `ensureTemplate` would mark each one "no longer available" on the way past.
 * The registry write comes last for the same reason it prunes statuses: it is
 * what clears the `idle` status `unload` leaves behind.
 */
export async function deleteUserTemplate(id: string): Promise<void> {
  await templateStore.remove(id);
  const st = useStore.getState();
  const gone = st.templates.find((t) => t.template.id === id)?.template;
  const remaining = st.templates.filter((t) => t.template.id !== id);
  // A blank of the same VIEW, since a job on a back blank replaced by a front
  // one would render the design onto the wrong half of the garment — the style
  // it belonged to first, then any built-in of that view, then any built-in at
  // all, and only if the shop has no built-ins, whatever is left.
  const builtins = remaining.filter((t) => t.kind === 'builtin');
  const sameView = gone ? builtins.filter((t) => t.template.view === gone.view) : [];
  const fallback = (sameView.find((t) => gone && t.template.style === gone.style) ?? sameView[0] ?? builtins[0])?.template.id
    ?? defaultTemplateId(remaining) ?? null;
  if (fallback) {
    for (const doc of st.docs) {
      const job = doc.mockup?.job;
      if (!job || job.templateId !== id) continue;
      // `{ undo: false }`: this is the app repairing a job the deletion broke, not an
      // edit the shop made. Recorded as undoable, the next Ctrl+Z — or the Undo on
      // whatever toast is up — puts the document straight back onto the blank that no
      // longer exists, and the footer answers in red while the toast says "Undid
      // Template". There is no undo for a delete; the repair must not pretend there is.
      useStore.getState().setMockupJob(doc.id, { ...job, templateId: fallback }, { undo: false });
    }
  }
  await mockupRunner.unload(id);
  useStore.getState().setTemplates(remaining);
}

/**
 * Import a backdrop image: decoded once in the worker (which is where the size comes from, and
 * which leaves it holding the pixels so the first render needs no second load), then stored, then
 * put in the list the panel and `sanitizeJob` read.
 *
 * The worker BEFORE the store, unlike a template save, because the row cannot be written without
 * the decoded size — and a store that then refuses the write (quota, a private window) would leave
 * the worker holding a scene nothing can name, so that case drops it again and the caller is told.
 *
 * Throws on a file past `SCENE_MAX_BYTES` before anything is read: a 60 MB backdrop is a decode
 * that would stall the worker for seconds and a row IndexedDB may well refuse.
 */
export async function addScene(file: File): Promise<SceneSource> {
  if (file.size > SCENE_MAX_BYTES) throw new Error('Scene image is larger than 25 MB');
  const id = crypto.randomUUID();
  const bytes = new Uint8Array(await file.arrayBuffer());
  const { width, height } = await engine().mockupLoadScene(id, transferBytes(bytes));
  // The worker holds it from here, so the status is the truth rather than an optimistic guess: the
  // first render that names this scene goes straight through instead of waiting for a decode.
  useStore.getState().setSceneStatus(id, { state: 'ready', error: undefined });
  const scene: SceneSource = { id, name: sceneName(file.name), width, height, bytes: file };
  try {
    await sceneStore.put(scene);
  } catch (err) {
    await mockupRunner.unloadScene(id).catch(() => {});
    // Named rather than rethrown raw: `withStore` rejects with the transaction's own `DOMException`,
    // whose `message` Chromium leaves EMPTY for `QuotaExceededError` — and the panel prints
    // `e.message`, so a storage-full import used to show a red line with nothing in it.
    const e = err as { name?: string; message?: string };
    throw new Error(e?.name === 'QuotaExceededError'
      ? 'Scene could not be stored: browser storage is full'
      : `Scene could not be stored: ${e?.message || e?.name || 'unknown error'}`);
  }
  useStore.getState().setScenes([...useStore.getState().scenes, scene]);
  return scene;
}

/**
 * Drop a backdrop: out of storage, off every job using it, out of the worker and out of the list —
 * in that order, which is `deleteUserTemplate`'s for the same reasons.
 *
 * The jobs are cleared BEFORE the worker forgets the scene and before the list loses it. Both halves
 * matter: `unloadScene` writes an `idle` status, which notifies this module's own subscription, and a
 * job still naming the scene at that moment would have it decoded straight back in — and a job
 * naming a scene the list no longer holds is a render that fails rather than one that falls back to
 * the photo's own backdrop. `setMockupJob` is the path used, so the renders re-trigger themselves.
 */
export async function removeScene(id: string): Promise<void> {
  await sceneStore.remove(id);
  const st = useStore.getState();
  const remaining = st.scenes.filter((s) => s.id !== id);
  for (const doc of st.docs) {
    if (!doc.mockup) continue;
    // Silent for `deleteUserTemplate`'s reason: clearing a backdrop whose pixels have
    // gone is a repair. `writeJob` re-sanitises against the live scene list, so an
    // undo of it would restore nothing at all and still say "Undid Backdrop".
    if (sceneIdOf(doc.mockup.job) === id) useStore.getState().setMockupJob(doc.id, sanitizeJob(doc.mockup.job, undefined, remaining), { undo: false });
    // Re-read: the write above notifies the mockup subscription synchronously, which re-derives
    // `backJob` from the job it just cleared — so by now the pair may already be in step.
    const backJob = useStore.getState().docs.find((d) => d.id === doc.id)?.mockup?.backJob;
    if (backJob && sceneIdOf(backJob) === id) useStore.getState().setDocMockup(doc.id, { backJob: sanitizeJob(backJob, undefined, remaining) });
  }
  await mockupRunner.unloadScene(id);
  useStore.getState().setScenes(remaining);
}

/**
 * True when any document this job's extra prints name has moved since the last
 * pass — landed a new run, or been CLOSED — so the render is out of date even
 * though nothing about this document changed.
 *
 * A print carrying `docId: null` (or this document's own id) prints this
 * document's art, which the plain run check above already covers.
 *
 * A closed document counts: the job keeps the print (the shop chose that design,
 * and switching back must not have cost them it), so the only way the preview
 * stops showing art nobody has open any more is a re-render — which reports the
 * area as skipped, and the panel names it.
 */
function extraArtMoved(job: MockupJob, selfId: string, st: AppState, prev: AppState): boolean {
  for (const p of job.extraPrints ?? []) {
    if (!p.enabled || !p.docId || p.docId === selfId) continue;
    const before = prev.docs.find((d) => d.id === p.docId);
    const now = st.docs.find((d) => d.id === p.docId);
    if (!now) { if (before) return true; continue; }
    if (now.run && now.run !== before?.run) return true;
  }
  return false;
}

// Keep every open document's mockup in step with the DTF output it composites,
// its own job and the template it renders into, while in mockup mode.
//
// Entering mockup mode (or a doc becoming ready while already in it) seeds a job
// from the shop defaults on the remembered template — or the first blank of the
// first style — so the panel has something to show without the user choosing
// first. The seed is a store write, which re-enters this subscription; that pass
// prepares the template and triggers the first render, which keeps the seeding
// and the rendering paths from having to duplicate each other's guards.
//
// `ensureTemplate` is called on every pass rather than only on the seed: it is a
// no-op once the template is ready or in flight, and it is what recovers a
// document whose template was unloaded (a user template re-saved from the editor).
const unsubMockup = useStore.subscribe((st, prev) => {
  if (st.appMode !== 'mockup') return;
  const justEnteredMockup = prev.appMode !== 'mockup';
  for (const doc of st.docs) {
    if (doc.status !== 'ready') continue;
    const prevDoc = prev.docs.find((d) => d.id === doc.id);
    if (!doc.mockup) {
      // Seeded only once the document HAS an output: the seed places the art at
      // the width this document actually prints at, which is measured from that
      // output. A document opened in mockup mode is therefore seeded by the pass
      // its first run lands in, not by the one that marked it ready.
      if (!doc.run) continue;
      // `st` is the snapshot this pass was called with, and every seed below is
      // a store write that re-enters this subscription synchronously — so by the
      // time the outer loop reaches this document, a nested pass may already have
      // seeded it. Re-read before writing, or it is seeded twice (the second
      // discarding the first's colours and back design).
      if (useStore.getState().docs.find((d) => d.id === doc.id)?.mockup) continue;
      const templateId = defaultTemplateId(st.templates, st.mockupDefaults.lastTemplateId);
      // No blanks at all (the fetch failed, or this is a fresh private window
      // with none of its own): nothing to seed with; the panel says so.
      if (templateId) useStore.getState().setDocMockup(doc.id, mockupFor(undefined, st.mockupDefaults, templateId, st.settings, doc.run));
      continue;
    }
    const templateId = doc.mockup.job.templateId;
    ensureTemplate(templateId);
    // The job's backdrop needs the worker to hold its pixels exactly as the blank needs its maps,
    // and for the same reason it is called on every pass: a no-op once the scene is decoded or in
    // flight, and it is what recovers a document whose scene was dropped.
    const jobSceneId = sceneIdOf(doc.mockup.job);
    ensureScene(jobSceneId);

    // Remember a template the user actually chose, so the next document (and the
    // next session) seeds on it. Never on the seed itself, which would only be
    // writing back what `defaultTemplateId` just read.
    if (prevDoc?.mockup && prevDoc.mockup.job.templateId !== templateId && st.mockupDefaults.lastTemplateId !== templateId) {
      useStore.getState().setMockupDefaults({ lastTemplateId: templateId });
    }

    // Decided rather than acted on immediately (and no `continue` between here
    // and the back half below): the pair's own render is triggered on the same
    // pass, and an early return for the front used to skip it entirely.
    let renderFront = false;
    // The template finished preparing — or the job's backdrop scene finished decoding: this is the
    // pass that turns the trigger a mode entry or a seed could not act on into a real render.
    if (becameReady(st, prev, templateId, jobSceneId)) {
      renderFront = true;
    } else if (justEnteredMockup) {
      // Two ways a document can be behind on entry, and the second is not
      // visible in `runGeneration` at all:
      //  - a DTF edit made in another mode landed a new run that nothing here
      //    was listening for (`doc.run === prevDoc.run` across the switch, so the
      //    run-change branch below never fires either);
      //  - a render was OWED and never started — the template was still preparing
      //    when it was asked for and became ready while the user was elsewhere,
      //    so no pass ever turned it into a render. `rendering` is still set and
      //    the reserved generation was never applied.
      // A redundant trigger costs nothing: `reserve` makes any render already in
      // flight stale the moment it lands.
      //  - a document this one PRINTS was re-run in the other mode. There is no
      //    generation for that on this document to compare (`runGeneration` is
      //    its own output's), so a job with an extra print of somebody else's
      //    art is simply re-rendered on entry: a redundant render is much the
      //    cheaper mistake against a sleeve showing art nobody has printed
      //    since.
      const m = doc.mockup;
      const owed = m.rendering || m.renderGeneration !== m.renderedGeneration;
      const printsAnother = (m.job.extraPrints ?? []).some((p) => p.enabled && p.docId && p.docId !== doc.id);
      if (doc.run && (owed || printsAnother || m.runGeneration !== doc.run.generation)) renderFront = true;
    } else if (prevDoc) {
      // A new run, then: just seeded (the re-entrant pass described above), or
      // edited in the panel.
      if (doc.run && doc.run !== prevDoc.run) renderFront = true;
      else if (!prevDoc.mockup || mockupChangeKind(prevDoc.mockup.job, doc.mockup.job) === 'render') renderFront = true;
      // …or a document this one PRINTS moved. An extra print composites another
      // document's DTF output into a second area, and nothing in this document's
      // own state changes when that output is re-run — so without this the
      // sleeve kept the art it was bound to at the moment it was added.
      else if (extraArtMoved(doc.mockup.job, doc.id, st, prev)) renderFront = true;
    }
    if (renderFront) mockupRunner.trigger(doc.id);

    // ---- the back half of a pair -------------------------------------------
    // The back design is a SEPARATE document with a job of its own, previewed in
    // its own panel on a FRONT blank (the only kind the panel offers). What the
    // pair shows — in the viewer's Back view and in the front+back export alike
    // — is that document's art on the style's BACK blank, in the colour this
    // document's front tile wears: `backJobFor`, derived here so that preview
    // and export cannot disagree (they used to: the toggle showed the back
    // design on a front blank).
    const backId = doc.mockup.backDocId;
    const backDoc = backId ? st.docs.find((d) => d.id === backId) : undefined;
    const front = st.templates.find((t) => t.template.id === templateId)?.template;
    // Its own preview blank still has to be prepared: that document's panel and
    // canvas render it there whenever it is the active one.
    if (backDoc?.mockup) ensureTemplate(backDoc.mockup.job.templateId);
    const backJob = backDoc?.mockup && front ? backJobFor(st.templates, front, doc.mockup.job, backDoc.mockup.job) : undefined;
    if (backJob) { ensureTemplate(backJob.templateId); ensureScene(sceneIdOf(backJob)); }
    // Read back live rather than from `st`: `ensureTemplate` above writes a
    // `preparing` status, which notifies this subscription synchronously, and
    // that nested pass may already have derived and written the same job — the
    // same re-entrancy the seed above re-reads for.
    const heldBackJob = useStore.getState().docs.find((d) => d.id === doc.id)?.mockup?.backJob;
    if (!sameMockupJob(heldBackJob, backJob)) {
      // Nothing will read the cached bitmaps again once the pair is unbound (or
      // the style loses its back blank), and they are up to 9 MB each — the
      // render, and the art-free blank a drag on that view ghosted over.
      if (!backJob) { dropMockupPreview(doc.id, 'back'); dropMockupBlank(doc.id, 'back'); }
      // Unbinding also moves the back slot's generation: a render still in flight
      // for the old pair must land as stale (closed, not cached), or it would be
      // drawn for the next pair bound during its own first debounce window.
      useStore.getState().setDocMockup(doc.id, backJob ? { backJob } : { backJob, backRendering: false, backRenderGeneration: nextGeneration() });
      // The write re-enters this subscription, but only this field moved, so
      // that pass finds nothing to do and the trigger here is the only one.
      if (backJob) mockupRunner.trigger(doc.id, 'back');
      continue;
    }
    if (!backJob) continue;
    const prevBack = backId ? prev.docs.find((d) => d.id === backId) : undefined;
    const m = doc.mockup;
    if (becameReady(st, prev, backJob.templateId, sceneIdOf(backJob))) {
      mockupRunner.trigger(doc.id, 'back');   // the back blank — or the pair's backdrop — landed
    } else if (backDoc?.run && prevBack && backDoc.run !== prevBack.run) {
      mockupRunner.trigger(doc.id, 'back');   // the back design's own output moved
    } else if (justEnteredMockup && (m.backRendering || m.backRenderGeneration !== m.backRenderedGeneration
      || (!!backDoc?.run && m.backRunGeneration !== backDoc.run.generation))) {
      mockupRunner.trigger(doc.id, 'back');   // the same catch-up the front gets
    }
  }
});

// re-run every open document when steps or settings change — except a bare
// vendor-preset change, which only the inspector reads, so it re-inspects the
// output already in the worker instead of re-running the whole pipeline.
//
// Deliberately NOT suppressed while a batch is zipping (`store.batchBusy`):
// locking the whole UI for the minutes a large batch takes is worse than the
// problem it would solve. Batch consistency comes from `lib/batch.ts` instead —
// it snapshots each doc's `run.generation` at click time and leaves out (with a
// reason) any doc whose generation moved, so an edit made mid-batch simply does
// not land in that archive.
const unsubRuns = useStore.subscribe((st, prev) => {
  if (st.steps === prev.steps && st.settings === prev.settings) return;
  const inspectOnly = st.steps === prev.steps && onlyPresetChanged(st.settings, prev.settings);
  for (const d of st.docs) if (d.status === 'ready') (inspectOnly ? runner.inspectOnly : runner.trigger)(d.id);
});

/** Is this document still open? Every await in the open path can be outlived by the × button. */
const stillOpen = (id: string) => useStore.getState().docs.some((d) => d.id === id);

export async function openFiles(files: File[]): Promise<void> {
  for (const file of files) {
    const id = newDocId();
    useStore.getState().addDoc(id, file.name, file.size);
    if (!isSupportedName(file.name)) { useStore.getState().setDocStatus(id, { status: 'error', error: 'Unsupported file type: use PNG, PSD, JPG or WebP.' }); continue; }
    let opened = false;
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      // Closed while we were reading the file: `open` was never posted, so the
      // worker has nothing for this id and `closeDoc`'s close() was a no-op.
      if (!stillOpen(id)) continue;
      const meta = await engine().open(id, file.name, transferBytes(bytes));
      opened = true;
      // Closed while `open` was in flight: `closeDoc` posted close() BEFORE the
      // worker finished creating the Doc, so it deleted nothing and the
      // full-resolution source would be retained forever. Close it again now
      // that it exists.
      if (!stillOpen(id)) { void engine().close(id); continue; }
      const src = await engine().preview(id, 'source', PREVIEW_MAX);
      if (!stillOpen(id)) { src?.bitmap.close(); void engine().close(id); continue; }
      useStore.getState().setDocStatus(id, { status: 'ready', meta });
      if (src) previews.set(id, { source: src });
      useStore.getState().bumpPreview(id);
      runner.trigger(id);
    } catch (e) {
      // A throw from `arrayBuffer()` or `open()` leaves nothing in the worker,
      // but a throw from `preview()` does not: `open` already created the Doc
      // and only an explicit × would ever free it. Close it here instead.
      if (opened) void engine().close(id);
      // setDocStatus is a no-op for a doc that is already gone.
      useStore.getState().setDocStatus(id, { status: 'error', error: (e as Error).message });
    }
  }
}

/**
 * How long a closed document is kept whole — its entry in `closedDocs`, its
 * preview bitmaps, its state in the worker — so Undo can put it straight back
 * (audit #14: the × dropped a file with no confirmation and no way back).
 *
 * Ten seconds, not longer: the full-resolution source in the worker is tens of
 * megabytes, and a shop that closed the wrong file notices at once.
 */
export const CLOSE_HOLD_MS = 10_000;

/** The pending drop for each held document, so a second close (or a flush) can
 *  bring it forward. */
const closeHolds = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * End a document's grace period: free its pixels and its worker state, and
 * forget the undo that would have brought it back.
 *
 * Does nothing at all if Undo got there first — the entry is back in `docs`, and
 * everything it needs is still exactly where it was.
 */
async function dropHeldDoc(id: string): Promise<void> {
  const timer = closeHolds.get(id);
  if (timer !== undefined) clearTimeout(timer);
  closeHolds.delete(id);
  const st = useStore.getState();
  if (st.docs.some((d) => d.id === id)) { st.forgetClosedDoc(id); return; }   // undone: keep the lot
  if (!st.closedDocs.some((c) => c.id === id)) return;                        // already freed
  // Forgotten BEFORE the pixels go, so there is never a moment where Ctrl+Z
  // would report a document restored and hand back an entry with nothing behind it.
  // Everything this document's history holds goes with it, not just the close: a
  // placement, a colourway or an ink list restores into a document nobody can open
  // any more, so the undo would do nothing while the app said "Undid Width".
  forgetUndo(closedDocKey(id));
  forgetUndoWhere((key) => key.startsWith(`job:${id}:`) || key === `colours:${id}`
    || key.startsWith(`inks:${id}:`) || key === `template:${id}`);
  st.forgetClosedDoc(id);
  dropDoc(id);
  // The unavailable-engine stub rejects every call; a failed close has nothing to show the user.
  try { await engine().close(id); } catch { /* worker gone or unavailable: nothing to free */ }
}

/** Free every held document now. The grace period is for a person; a test — or a
 *  shop closing a second file — has no use for it. */
export async function flushClosedDocs(): Promise<void> {
  for (const id of [...closeHolds.keys()]) await dropHeldDoc(id);
}

export async function closeDoc(id: string): Promise<void> {
  const doc = useStore.getState().docs.find((d) => d.id === id);
  // A hold left over from an EARLIER close of this same document — closed, undone,
  // closed again — is cancelled here. Without this its timer keeps running against
  // the new close and frees the pixels a fraction of a second into a ten-second
  // grace period.
  const stale = closeHolds.get(id);
  if (stale !== undefined) { clearTimeout(stale); closeHolds.delete(id); }
  // FIRST, and synchronously: this is the file the × was pressed on, and it leaves
  // the list before anything is awaited. Sets the entry aside (and pushes the undo
  // entry) instead of dropping it; the pixels and the worker doc follow in
  // `CLOSE_HOLD_MS` unless Undo asks for them.
  useStore.getState().removeDoc(id);
  // Never open (or already closed): nothing to hold. A document still LOADING is not
  // held either — the open path abandoned it the moment it left `docs` (its worker
  // copy was closed and the source bytes are gone), so putting it back would add a
  // row that spins for ever. It says what it closed, without an Undo it cannot keep.
  if (!doc || doc.status === 'loading') {
    if (doc) useStore.getState().showToast(`Closed ${doc.name}`);
    dropOtherHolds(id);
    await dropHeldDoc(id);
    return;
  }
  useStore.getState().showToast(`Closed ${doc.name}`, { undoToken: topUndoToken() });
  closeHolds.set(id, setTimeout(() => { void dropHeldDoc(id); }, CLOSE_HOLD_MS));
  dropOtherHolds(id);
}

/**
 * Free every held document but this one — one is recoverable at a time, rather than
 * two full-resolution sources sitting in the worker.
 *
 * NOT awaited, and that is the whole point: a Web Worker handles one message at a
 * time, so `close(A)` queues behind whatever it is doing — a five-second run on a
 * large file, a films export, a batch encode. Awaited, the second × did nothing
 * visible for those seconds: the file stayed in the list with no toast. Everything
 * `dropHeldDoc` does to the store and the bitmap caches happens synchronously
 * before its own await, so the only thing left running here is the worker's own
 * bookkeeping, which nothing on screen is waiting for.
 */
function dropOtherHolds(id: string): void {
  for (const other of [...closeHolds.keys()]) if (other !== id) void dropHeldDoc(other);
}

/**
 * Undo the last change and say what it was. The toast is the whole of how a key
 * press that changed something off-screen — a colour back in a folded section, a
 * file back in the list — is visible at all.
 */
export function undoLast(): void {
  const st = useStore.getState();
  const label = undo(undoScope());
  // "…here" when the stack is not empty but nothing on it belongs to this document
  // in this mode: the honest answer to a press that did nothing, and the one that
  // says where the missing step went (back to the mode or the file it was made in).
  st.showToast(label ? undoneMessage(label) : canUndo() ? 'Nothing to undo here' : 'Nothing to undo');
}

/** What Ctrl+Z and a toast's Undo are allowed to reach: the document on screen, in
 *  the mode on screen. See `UndoScope` for why an entry outside it is left alone. */
function undoScope(): UndoScope {
  const st = useStore.getState();
  return { docId: st.activeId, mode: st.appMode };
}

/**
 * Undo the change a toast is ABOUT, whatever has happened since — what its Undo
 * button calls. An entry that is no longer on the stack says so rather than
 * quietly reversing somebody else's edit.
 */
export function undoToast(token: UndoToken): void {
  const label = undoTo(token, undoScope());
  useStore.getState().showToast(label ? undoneMessage(label) : 'That change can no longer be undone');
}

/**
 * Ctrl+Z / Cmd+Z, app-wide. Not while a text field has focus: there the browser's
 * own undo is the only thing that can put a half-typed word back, and taking the
 * key away from it would be a worse trade than the one this exists to fix.
 */
function onUndoKeydown(e: KeyboardEvent): void {
  if (!isUndoShortcut(e) || isTextEntry(document.activeElement as HTMLInputElement | null)) return;
  // Taken from the browser either way, so its own undo cannot fire on the range input
  // or the colour chip the key arrived on.
  e.preventDefault();
  // ...but NOT acted on while the template editor (or any other modal) is up: the app
  // behind the backdrop is not what the shop is working on, the editor has no undo of
  // its own, and a placement quietly reversed back there is only discovered on Cancel.
  if (inModalDialog((e.target as Element | null) ?? document.activeElement, document)) return;
  undoLast();
}
if (typeof window !== 'undefined') window.addEventListener('keydown', onUndoKeydown);

/**
 * A document Undo has just put back: ask for the work its close threw away.
 *
 * Every runner discards a result whose document has left `docs`, so the run, the
 * inspection, the plate build and the render that were in flight at the × are gone
 * — and nothing else would re-trigger them. The DTF runner only runs on a
 * steps/settings change; `unsubSep` and `unsubMockup` both need a `prevDoc` (there
 * is none for a document that has just reappeared) or a mode entry. Without this
 * the file comes back reading "· running" for ever, with no output, and Export all
 * (ZIP) stays disabled for every document in the app.
 *
 * `settleDoc` has already cleared the flags; `doc` here is the entry as it was
 * closed, which is the only record of what was owed. A redundant trigger is the
 * much cheaper mistake — `reserve` makes anything already in flight stale — so a
 * document with no run at all is run too.
 */
onDocRestored((doc: DocEntry) => {
  if (doc.status !== 'ready') return;
  if (doc.running || doc.inspecting || !doc.run) runner.trigger(doc.id);
  if (doc.sep?.building) sepRunner.trigger(doc.id);
  if (doc.mockup?.rendering) mockupRunner.trigger(doc.id);
  if (doc.mockup?.backRendering) mockupRunner.trigger(doc.id, 'back');
});

// Vite HMR re-evaluates this module in dev; without this the old subscriptions and
// runner stay alive and every change runs the pipeline twice.
import.meta.hot?.dispose(() => {
  unsubPersist(); unsubRuns(); unsubSep(); unsubMockup(); runner.dispose(); sepRunner.dispose(); mockupRunner.dispose();
  if (typeof window !== 'undefined') window.removeEventListener('keydown', onUndoKeydown);
});
